<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import _ from 'lodash'

import { successState } from '@/components/scenario-loading/constants/progressState'

import { useScenarioStore } from '@/stores/scenario'
import { useProjectStore } from '@/stores/project'
import { useMapStore } from '@/stores/map'

import ResultTitle from '@/components/scenario-result/ResultTitle.vue'
import ScenarioLoading from '@/components/scenario-loading/ScenarioLoading.vue'
import ArcGisMapComponent from '@/components/map/ArcGisMapComponent.vue'

const route = useRoute()

const scenarioStore = useScenarioStore()
const projectStore = useProjectStore()
const mapStore = useMapStore()

const { currentItem } = storeToRefs(scenarioStore)

const calculationStatus = ref<string>('')

const loading = ref(true)

const scenarioId = computed(() => parseInt(route.params.scenarioId as string))

const layerUrls = ref<string[]>([])
const baseNameMap = ref<string>('')
const baseMapId = ref<number | null>(null)
const comparisonLayerUrls = ref<string[]>([])
const comparisonBaseNameMap = ref<string>('')
const arcgisLocationPlatformToken = ref<string | null>(null)

const statusChoices = ref<{ value: string; display_name: string }[]>([])
const doneStatusValue = computed(() => {
  return statusChoices.value.find((choice) => choice.display_name === 'Done')?.value || 'Done'
})

const fetchFilterItems = async () => {}

const updateQueryParams = async () => {}

const selectedCalculations = ref<number>()

const handleSelectedCalculationsUpdate = (newSelection: number) => {
  selectedCalculations.value = newSelection
}

const applyFilter = async () => {
  await fetchFilterItems()
  await updateQueryParams()
}

const clearAllFilter = async () => {
  await fetchFilterItems()
  await updateQueryParams()
}

const updateCalculationStatus = (value: string) => {
  calculationStatus.value = value
}

const handleViewingOptionUpdate = (newOption: string) => {
  if (newOption === 'one') {
    selectedCalculations.value = undefined
  }
}

const fetchMapDetails = async () => {
  if (scenarioId.value) {
    const mapDetail = await mapStore.fetchMapDetail(scenarioId.value)
    if (mapDetail && mapDetail.layer_urls) {
      layerUrls.value = mapDetail.layer_urls
      baseNameMap.value = mapDetail.name || 'Simulation 01'
      baseMapId.value = mapDetail.id || null
    } else {
      console.warn('No layer URLs found for the current calculation.')
    }
  }
}

const fetchComparisonMapDetails = async (comparisonId: number) => {
  const mapDetail = await mapStore.fetchMapDetail(comparisonId)
  if (mapDetail && mapDetail.layer_urls) {
    comparisonLayerUrls.value = mapDetail.layer_urls
    comparisonBaseNameMap.value = mapDetail.name || 'Simulation 02'
  } else {
    comparisonLayerUrls.value = []
  }
}

watch(
  () => calculationStatus.value,
  async (newValue: string) => {
    if (newValue === successState) {
      await fetchMapDetails()
    }
  }
)

watch(selectedCalculations, async (newValue, oldValue) => {
  if (newValue) {
    await fetchComparisonMapDetails(newValue)
  } else {
    comparisonLayerUrls.value = []
  }
})

onMounted(async () => {
  loading.value = true

  await scenarioStore.fetchOptions()

  if (scenarioId.value) {
    await scenarioStore.fetchItem(scenarioId.value)
  }

  const options = await scenarioStore.getScenarioOptions?.()
  statusChoices.value = options?.actions?.POST?.status?.choices || []

  if (currentItem.value?.project) {
    const { project } = currentItem.value
    await projectStore.fetchOptions()
    await projectStore.fetchItem(project)
  }

  if (scenarioId.value) {
    arcgisLocationPlatformToken.value = await scenarioStore.getArcgisLocationPlatformToken(
      scenarioId.value
    )
  }

  if (currentItem.value?.status === doneStatusValue.value) {
    calculationStatus.value = successState
  }

  loading.value = false
})

onUnmounted(() => {
  scenarioStore.resetAll()
  layerUrls.value = []
  baseNameMap.value = ''
  baseMapId.value = null
  comparisonLayerUrls.value = []
  comparisonBaseNameMap.value = ''
  mapStore.resetAll()
})
</script>

<template>
  <ScenarioLoading
    v-if="
      loading || (calculationStatus !== successState && currentItem?.status !== doneStatusValue)
    "
    @update-calculation-status="updateCalculationStatus"
  />
  <div
    v-else
    style="display: flex; flex-direction: column"
    class="calculation-result-container fill-height"
  >
    <ResultTitle
      v-if="currentItem"
      :mapId="baseMapId ?? undefined"
      @apply-filter="applyFilter"
      @clear-all-filter="clearAllFilter"
      @update-selected-calculations="handleSelectedCalculationsUpdate"
      @update-viewing-option="handleViewingOptionUpdate"
    />
    <div class="map-container mt-2">
      <ArcGisMapComponent
        v-if="layerUrls.length > 0"
        :layerUrls="layerUrls"
        :comparisonLayerUrls="comparisonLayerUrls"
        :baseName="baseNameMap"
        :comparisonBaseName="comparisonBaseNameMap"
        :baseMapId="baseMapId ?? undefined"
        :arcgisLocationPlatformToken="arcgisLocationPlatformToken || ''"
      />
    </div>
  </div>
</template>

<style scoped>
.calculation-result-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
}

.map-container {
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: auto;
  justify-content: center;
  align-items: center;
  display: flex;
  position: relative;
}

:deep(.app-container) {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

:deep(.arcgis-map) {
  height: 100%;
  width: 100%;
  min-height: 500px;
}
</style>
